/**
 * Animation Tokens
 * 
 * This file defines the base animation tokens for use across platforms.
 * Platform-specific implementations should adapt these base tokens.
 */

// Define the animation timings
export const animationTimings = {
  fast: 0.2,        // 200ms
  medium: 0.4,      // 400ms
  slow: 0.6,        // 600ms
  extraSlow: 0.8,   // 800ms
};

// Define stagger intervals for sequences
export const staggerIntervals = {
  tight: 0.05,      // 50ms
  default: 0.1,     // 100ms
  loose: 0.15,      // 150ms
  extraLoose: 0.2,  // 200ms
};

// Define animation delays
export const animationDelays = {
  none: 0,
  short: 0.1,       // 100ms
  medium: 0.3,      // 300ms
  long: 0.5,        // 500ms
  extraLong: 0.8,   // 800ms
};

// Define animation easing curves
export const easings = {
  // Standard easings
  linear: [0, 0, 1, 1],
  easeIn: [0.4, 0, 1, 1],
  easeOut: [0, 0, 0.6, 1],
  easeInOut: [0.4, 0, 0.6, 1],
  
  // Premium easings for more natural motion
  premium: {
    easeOutQuint: [0.22, 1, 0.36, 1],
    easeOutExpo: [0.16, 1, 0.3, 1],
    easeOutBack: [0.34, 1.56, 0.64, 1],
    elasticOut: [0.64, 0.57, 0.67, 1.53],
    bounceOut: [0.22, 1.2, 0.36, 1]
  }
};

// Define animation presets for common patterns
export const animationPresets = {
  // Entry animations
  fadeIn: {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { duration: animationTimings.medium, ease: easings.easeOut }
    }
  },
  slideInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1,
      y: 0,
      transition: { duration: animationTimings.medium, ease: easings.premium.easeOutQuint }
    }
  },
  slideInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { 
      opacity: 1,
      x: 0,
      transition: { duration: animationTimings.medium, ease: easings.premium.easeOutQuint }
    }
  },
  slideInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { 
      opacity: 1,
      x: 0,
      transition: { duration: animationTimings.medium, ease: easings.premium.easeOutQuint }
    }
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { 
      opacity: 1,
      scale: 1,
      transition: { duration: animationTimings.medium, ease: easings.premium.easeOutBack }
    }
  },
  
  // Hover animations
  hover: {
    scale: {
      scale: 1.05,
      transition: { duration: animationTimings.fast, ease: easings.premium.easeOutBack }
    },
    lift: {
      y: -5,
      transition: { duration: animationTimings.fast, ease: easings.premium.easeOutQuint }
    },
    glow: {
      boxShadow: "0 15px 25px -5px rgba(0, 0, 0, 0.2)",
      transition: { duration: animationTimings.medium, ease: easings.easeOut }
    }
  },
  
  // Container stagger animations for parent elements
  staggerContainer: {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { 
        staggerChildren: staggerIntervals.default,
        delayChildren: animationDelays.short
      }
    }
  },
  
  // Spring physics presets (for frameworks that support spring animations)
  springs: {
    gentle: {
      type: "spring",
      stiffness: 150,
      damping: 15
    },
    responsive: {
      type: "spring",
      stiffness: 200,
      damping: 20
    },
    bouncy: {
      type: "spring", 
      stiffness: 300,
      damping: 10
    }
  }
}; 